import React, { useState, useEffect } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { useProgress, useSystemBadges, useUserBadges } from '../../hooks/queries';

const ProgressPage = () => {
  const { user } = useAuth();

  // 🟢 [P2] React Query：进度统计、系统徽章、我的徽章改为缓存化读取
  const { data: progressRes, isLoading: loadingProgress } = useProgress();
  const { data: systemBadges = [], isLoading: loadingSystemBadges } = useSystemBadges();
  const { data: userEarnedBadges = [], isLoading: loadingUserBadges } = useUserBadges();
  const progressData = progressRes ? (progressRes.progress || {}) : undefined;
  const loading = loadingProgress || loadingSystemBadges || loadingUserBadges;

  // 3. 计算展示数据 - 顶部统计卡片
  const stats = [
    { label: 'Words Learned', value: progressData?.totalWordsLearned || 0, icon: '📚', color: 'from-blue-500 to-blue-600' },
    { label: 'Lessons Completed', value: progressData?.totalLessonsCompleted || 0, icon: '✅', color: 'from-green-500 to-green-600' },
    { label: 'Games Played', value: progressData?.totalGamesPlayed || 0, icon: '🎮', color: 'from-purple-500 to-purple-600' },
    { label: 'Current Streak', value: `${user?.streak || 0} days`, icon: '🔥', color: 'from-orange-500 to-red-500' }
  ];

  // 4. 计算等级进度 (Core, Stretch, Genius)
  // 🟢 逻辑说明：优先使用后端返回的真实数据 (progressData)，如果后端没数据(undefined)，则使用默认值兜底
  const levelProgress = {
    core: progressData?.coreProgress || { completed: 0, total: 12, percentage: 0 },
    stretch: progressData?.stretchProgress || { completed: 0, total: 12, percentage: 0 },
    genius: progressData?.geniusProgress || { completed: 0, total: 12, percentage: 0 }
  };

  // 5. 徽章分类与排序逻辑
  // 提取用户已拥有的徽章ID列表
  const earnedBadgeIds = userEarnedBadges.map(ub => {
      // 如果是字符串（现有后端逻辑），直接返回
      if (typeof ub === 'string') return ub;
      // 如果是对象（预留扩展），返回 badgeId 或 id
      return ub.badgeId || ub.id;
  });

  // (A) 已获得的徽章列表
  const earnedList = systemBadges
    .filter(b => earnedBadgeIds.includes(b.id))
    .map(b => {
      // 尝试找到原始记录（如果是字符串存储，则没有 earnedAt 时间）
      const record = userEarnedBadges.find(ub => {
          if (typeof ub === 'string') return ub === b.id;
          return ub.badgeId === b.id;
      });
      
      return { 
          ...b, 
          // 如果 record 是字符串，earnedAt 为 null
          earnedAt: (typeof record === 'object') ? record.earnedAt : null 
      };
    });

  // (B) 下一个目标列表 (未获得的徽章)
  const nextList = systemBadges
    .filter(b => !earnedBadgeIds.includes(b.id))
    .sort((a, b) => {
      // 🟢 1. 定义排序优先级 (数字越小越靠前)
      // 映射规则：criteria.type -> 优先级
      // 如果没有 criteria 字段，尝试用 category 映射，或者放最后
      const getPriority = (badge) => {
        const type = badge.criteria?.type || '';
        const cat = (badge.category || '').toLowerCase();

        // 优先级 1: 课程类
        if (type === 'lessons_completed' || cat === 'learning') return 1;
        
        // 优先级 2: 单词类
        if (type === 'words_learned' || cat === 'vocabulary') return 2;
        
        // 优先级 3: 游戏类
        if (type === 'game_played' || type === 'survival_high_score' || cat === 'gaming') return 3;
        
        return 99; // 其他类型放最后
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      // 如果优先级不同，按优先级排
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // 🟢 2. 如果优先级相同（同类徽章），按分数 (Points) 升序排
      // 这样用户会先看到简单的任务 (50分)，后看到难的 (100分)
      return (a.points || 0) - (b.points || 0);
    });

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 to-blue-300">
      <div className="text-2xl font-bold text-white flex items-center">
        <AppIcon name="spinner" className="mr-3" />
        Loading Progress...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            📊 Your Progress
          </h1>
          <p className="text-xl text-gray-700">
            Track your vocabulary learning journey!
          </p>
        </motion.div>

        {/* Stats Overview Cards */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              whileHover={{ scale: 1.05 }}
              className={`bg-gradient-to-r ${stat.color} rounded-2xl p-6 text-white shadow-xl`}
            >
              <div className="text-4xl mb-3">{stat.icon}</div>
              <div className="text-3xl font-bold mb-1">{stat.value}</div>
              <div className="text-sm opacity-90">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Level Progress Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 mb-8 text-gray-800"
        >
          <h2 className="text-3xl font-bold mb-6 text-center">🎯 Level Progress</h2>
          <div className="space-y-6">
            {Object.entries(levelProgress).map(([level, data]) => (
              <div key={level} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xl font-semibold capitalize flex items-center">
                    {level === 'core' && '🌱'}
                    {level === 'stretch' && '🔥'}
                    {level === 'genius' && '🧠'}
                    <span className="ml-2">{level}</span>
                  </span>
                  {/* 🟢 这里显示的是后端返回的真实进度，如 1/12 lessons */}
                  <span className="text-sm">{data.completed}/{data.total} lessons</span>
                </div>
                <div className="w-full bg-white bg-opacity-20 rounded-full h-4">
                  <div 
                    className={`h-4 rounded-full transition-all duration-1000 ${
                      level === 'core' ? 'bg-green-500' :
                      level === 'stretch' ? 'bg-yellow-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${data.percentage}%` }}
                  ></div>
                </div>
                <div className="text-right text-sm text-gray-600">{Math.round(data.percentage)}% complete</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Badges Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          
          {/* Earned Badges Column */}
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 text-gray-800"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">🏅 Earned Badges</h2>
            {earnedList.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {earnedList.map((badge) => (
                  <div key={badge.id} className="flex items-center space-x-4 bg-white bg-opacity-40 rounded-lg p-4 shadow-sm border border-white/50">
                    <div className="text-4xl filter drop-shadow-md">{badge.icon}</div>
                    <div>
                      <div className="font-bold text-lg text-slate-800">{badge.name}</div>
                      <div className="text-sm text-slate-700">{badge.description}</div>
                      <div className="text-xs text-green-700 font-bold mt-1">
                        +{badge.points} points 
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-600 py-8">
                <div className="text-4xl mb-4">🏃‍♂️</div>
                <p>Start learning to earn your first badge!</p>
              </div>
            )}
          </motion.div>

          {/* Next Targets Column (Sorted by Points) */}
          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8 text-gray-800"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">
              🎯 Next Targets ({nextList.length})
            </h2>
            <div className="space-y-4 max-h-[500px] overflow-y-auto custom-scrollbar pr-2">
              {nextList.map((badge) => (
                <div key={badge.id} className="flex items-center space-x-4 bg-white bg-opacity-10 rounded-lg p-4 border border-white/30 hover:bg-opacity-20 transition-colors">
                  <div className="text-3xl opacity-50 grayscale">{badge.icon}</div>
                  <div>
                    <div className="font-bold text-slate-700">{badge.name}</div>
                    <div className="text-sm text-slate-600">
                      Requirement: <span className="font-medium">{badge.requirement}</span>
                    </div>
                    {/* 显示奖励积分 */}
                    <div className="text-xs text-blue-600 font-bold mt-1">Reward: {badge.points} pts</div>
                  </div>
                </div>
              ))}
              {nextList.length === 0 && (
                <div className="text-center text-gray-600 py-8">
                  🎉 Amazing! You've earned all available badges!
                </div>
              )}
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default ProgressPage;